import { createSelector } from 'reselect'
import { IMountainPlayerData } from '../../../helpers/data/mountainPlayer'
import { IMountainSortedPlayer } from '../../../helpers/data/mountainSortedPlayer'
import { IPlayerData } from '../../../helpers/data/player'
import { ICharacterData } from '../../../helpers/data/characterData'
import playerDataSelector from '../app/playerDataSelector'
import activitySelector from '../app/activitySelector'
import userIdSelector from '../app/userIdSelector'
import viewTypes from '../../../helpers/enums/views'
import gameStates from '../../../helpers/enums/gameStates'
import settings from '../../../helpers/enums/settings'
import hideStudentNamesSelector from '../app/hideStudentNamesSelector'
import gameModeSelector from '../app/gameModeSelector'
import hiddenNameText from '../../../helpers/enums/hiddenNameText'
import charactersSelector from '../app/charactersSelector'
import skinSelector from '../app/skinSelector'
import { IStore } from '../../reducers/index'
import playerScore from './helpers/playerScore'

const getMinMaxScores = (campPlayers: IMountainPlayerData[]) => {
	let min = Number.MAX_SAFE_INTEGER,
		max = -1
	campPlayers.forEach((player: IMountainPlayerData) => {
		min = Math.min(min, player.score)
		max = Math.max(max, player.score)
	})
	return {
		min,
		max,
	}
}

const playersSelector = (state: IStore) => {
	const players = state.game.participants
	const activities = state.game.activities
	const characters = charactersSelector(state)
	const shouldHideStudentNames = hideStudentNamesSelector(state)
	const gameMode = gameModeSelector(state)
	const playerUID = userIdSelector(state)
	const localPlayerData = playerDataSelector(state)
	const localPlayerActivityUid =
		localPlayerData.view.type === viewTypes.GAME ? localPlayerData.view.payload.activityUid : ''
	const maxPlayerCount = 50

	let campPlayers = players
		.filter((player: any) => [viewTypes.WINNERS, viewTypes.GAME].includes(player.view.type))
		.sort((a: IPlayerData, b: IPlayerData) => {
			return a.order - b.order
		})
	const currentPlayerIndex = campPlayers.findIndex((player) => player.deviceUid === playerUID)
	campPlayers = campPlayers
		.reduce((accumulator: any[], player: IPlayerData, index: number): IPlayerData[] => {
			if (index > currentPlayerIndex - maxPlayerCount && accumulator.length < maxPlayerCount) {
				accumulator.push(player)
			}
			return accumulator
		}, [])

		.map((player: IPlayerData, index: number): IMountainPlayerData => {
			const characterData =
				characters.find((character: ICharacterData) => character.id === player.avatar) || characters[0]
			const playerPayload = player.view.payload || {}

			return {
				answered: playerPayload.isGameAnswered,
				answersUids: playerPayload.answersUids,
				isAnswerCorrect:
					playerPayload.answersUids &&
					playerPayload.correctAnswersUids &&
					playerPayload.answersUids.sort().join(',') === playerPayload.correctAnswersUids.sort().join(','),
				avatarAsset: Array.isArray(characterData.game) ? characterData.game : [characterData.game],
				avatarId: player.avatar,
				avatarIcon: characterData.loginIcon,
				label: shouldHideStudentNames && player.deviceUid !== playerUID ? hiddenNameText : player.nickname,
				lane: index,
				nickname: shouldHideStudentNames && player.deviceUid !== playerUID ? hiddenNameText : player.nickname,
				participantID: player.uid,
				lastActivityUid: playerPayload.currentActivityUid,
				playerUID: player.deviceUid,
				pos: 0,
				score: playerScore(player, activities, localPlayerActivityUid, gameMode),
			}
		})
	const minMaxScores = getMinMaxScores(campPlayers)

	campPlayers = campPlayers.map((player: IMountainPlayerData) => ({
		...player,
		pos:
			minMaxScores.max === 0
				? 0
				: 0.25 + 0.75 * ((player.score - minMaxScores.min) / (minMaxScores.max - minMaxScores.min)),
	}))
	/*.sort((a:IMountainPlayerData, b:IMountainPlayerData) => {
		return b.score - a.score
	})*/
	return campPlayers
}

const activitiesSelector = (state: IStore) => {
	return state.game.activities
}

const sizeSelector = (state: IStore) => {
	return state.app.size
}

const studentsIDWithNumberOfCorrectAnswersFromServer = (state: IStore) => {
	return state.game.studentsIDWithNumberOfCorrectAnswersFromServer
}

const gameSettingsSelector = (state: IStore) => {
	return state.game.settings
}

const audioSelector = (state: IStore) => state.app.audio

const sortedPlayersSelector = (state: IStore) => {
	const players = state.game.participants
	const activities = state.game.activities
	const gameMode = gameModeSelector(state)
	const playerUID = userIdSelector(state)
	const shouldHideStudentNames = hideStudentNamesSelector(state)
	const localPlayerData = playerDataSelector(state)
	const localPlayerActivityUid =
		localPlayerData.view.type === viewTypes.GAME ? localPlayerData.view.payload.activityUid : ''

	const campPlayers = players
		.filter((player: any) => [viewTypes.WINNERS, viewTypes.GAME].includes(player.view.type))

	const allPlayersSortedByScore = [...campPlayers].sort((a: IPlayerData, b: IPlayerData) => {
		const scoreA = playerScore(a, activities, localPlayerActivityUid, gameMode)
		const scoreB = playerScore(b, activities, localPlayerActivityUid, gameMode)
		return scoreB - scoreA;
	})

	return allPlayersSortedByScore.map((player: IPlayerData): IMountainSortedPlayer => ({
		playerUID: player.deviceUid,
		label: shouldHideStudentNames && player.deviceUid !== playerUID ? hiddenNameText : player.nickname,
	}))
}

const animationsToggleSelector = (state: IStore) => state.app.animationsToggle

export default createSelector(
	playersSelector,
	activitySelector,
	activitiesSelector,
	playerDataSelector,
	sizeSelector,
	skinSelector,
	gameModeSelector,
	hideStudentNamesSelector,
	studentsIDWithNumberOfCorrectAnswersFromServer,
	gameSettingsSelector,
	audioSelector,
	userIdSelector,
	sortedPlayersSelector,
	animationsToggleSelector,
	(
		players,
		activity,
		activities,
		playerData,
		size,
		skin,
		gameMode,
		shouldHideStudentNames,
		studentsIDWithNumberOfCorrectAnswersFromServer,
		gameSettings,
		audio,
		playerUID,
		sortedPlayers,
		animationsToggle
	) => {
		const isPauseEnabledSetting = gameSettings.find((setting) => setting.id === settings.SETTING_TYPE_PAUSE)
		const isSoundEnabledSetting = gameSettings.find((setting) => setting.id === settings.SETTING_TYPE_AUDIO)
		const isTimerDisabledSetting = gameSettings.find((setting) => setting.id === settings.SETTING_TYPE_TIMER_DISABLED)

		const currentPlayerRank = sortedPlayers.findIndex(p => p.playerUID === playerUID) + 1

		const areAnimationsEnabledSetting = gameSettings.find((setting) => setting.id === settings.SETTING_TYPE_ANIMATIONS)

		// Toggle takes precedence if user has manually toggled
		const areAnimationsEnabled = animationsToggle.hasBeenToggled
			? animationsToggle.enabled
			: areAnimationsEnabledSetting
				? areAnimationsEnabledSetting.value === settings.SETTING_ANIMATIONS_ENABLED
				: true

		return {
			players,
			activity,
			totalActivities: activities.length,
			isFinished:
				playerData.view.payload && [gameStates.ENDED, gameStates.TRANSITIONING].includes(playerData.view.payload.state),
			playerData,
			size,
			totalTime: activity.timer ? activity.timer * 1000 : 30000,
			elapsedTime: activity.elapsedTime * 1000,
			skin: skin.game,
			gameMode,
			shouldHideStudentNames,
			isPauseEnabledSetting: isPauseEnabledSetting.value,
			isTimerDisabledSetting: isTimerDisabledSetting ? isTimerDisabledSetting.value : settings.SETTING_TIMER_DEFAULT,
			studentsIDWithNumberOfCorrectAnswersFromServer,
			isSoundEnabledSetting: isSoundEnabledSetting.value === settings.SETTING_GAME_AUDIO_UNMUTED,
			volume: audio.volume,
			isMuted: audio.isMuted,
			currentPlayerRank,
			sortedPlayers,
			areAnimationsEnabled,
		}
	}
)
